import { EventEmitter } from 'events'
import type { Procedure } from '../../../app-core/lib/procedure/Procedure'
import type { IAddress } from '../ui/location/addLocation'
import { EStatus } from '../utils/async'
import { Base_Dispatcher } from '../../zap/Base/Base.Dispatcher'
import type { Type_ICalendarEvent, Type_IProcessedLocation, Type_TTimelineEvent } from '../../zap/Type/Type.hive'
import {
    Loca_SEARCH_LOCATION,
    Loca_SELECT_EVENT,
    Loca_SELECT_LOCATION,
    Loca_SET_ADDRESS,
    Loca_SET_ADDRESS_FIELD,
    Loca_SET_DIALOG_CAMPAIGNS,
    Loca_SET_DIALOG_EVENTS,
    Loca_SET_DIALOG_OPEN,
    Loca_SET_DIALOG_TYPE,
    Loca_SET_DOWNLOADING_LOCATIONS,
    Loca_SET_LOCATIONS,
    Loca_SET_NEARBY_LOCATIONS,
    Loca_SET_REPORT_EVENT,
    Loca_SET_REPORT_FOCUSLIST,
    Loca_SET_SEARCH_LOCATIONS,
    Loca_SET_TIMELINE,
} from '../../zap/Loca/Loca.constants'

type LocationResult = { status: EStatus; locations: Type_IProcessedLocation[] }

export type TNearbyLocations =
    | { status: EStatus.loading }
    | { status: EStatus.success; locations: Type_IProcessedLocation[] }
    | { status: EStatus.failure; failureType: 'GPS' | 'Other' }

const CHANGE_EVENT = 'change'
let _locations: Type_IProcessedLocation[] = []
let _searchText: string = ''
let _nearbyLocations: TNearbyLocations = { status: EStatus.loading }
let _searchLocations: LocationResult = { status: EStatus.loading, locations: [] }
let _downloadingLocations: Set<number> = new Set()
let _address: IAddress = {
    name: '',
    street: '',
    city: '',
    postcode: '',
    state: '',
    xCoord: 0,
    yCoord: 0,
}
let _timeline: any = { status: EStatus.loading, events: [] }
let _selectedLocation: Type_IProcessedLocation = null
let _selectedEvent: Type_TTimelineEvent = null
let _reportEventId: number | string = null
let _reportFocusListId: number = null
let _dialogOpen: boolean = false
let _dialogType: 'event' | 'campaign' = 'event'
let _dialogEvents: Type_ICalendarEvent[] = []
let _dialogCampaigns: Procedure[] = []

class LocationStore extends EventEmitter {
    public setLocations(data: any) {
        _locations = data
    }

    public getLocations() {
        return _locations
    }

    public setNearbyLocations(data: TNearbyLocations) {
        _nearbyLocations = data
    }

    public getNearbyLocations() {
        return _nearbyLocations
    }

    public setSearchLocations(data: any) {
        _searchLocations = data
    }

    public getSearchLocations() {
        return _searchLocations
    }

    public setDownloadingLocations(data: Set<number>) {
        _downloadingLocations = data
    }

    public getDownloadingLocations() {
        return _downloadingLocations
    }

    public setAddress(data: IAddress) {
        _address = data
    }

    public setAddressField(field: keyof IAddress, value: any) {
        _address = {
            ..._address,
            [field]: value,
        }
    }

    public getAddress(): IAddress {
        return _address
    }

    public setTimeline(data: any) {
        _timeline = data
    }

    public getTimeline() {
        return _timeline
    }

    public getSearchText() {
        return _searchText
    }

    private getLocationById(index: number) {
        for (const item in _locations) {
            if (_locations[item].id === index) return _locations[item]
        }
        return null
    }

    public selectLocation(id: number) {
        _selectedLocation = this.getLocationById(id)
    }

    public getSelectedLocation() {
        return _selectedLocation
    }

    public searchLocation(string: string) {
        _searchText = string
    }

    public selectEvent(event: any) {
        _selectedEvent = event
    }

    public getSelectedEvent() {
        return _selectedEvent
    }

    public setReportEvent(id: number | string) {
        _reportEventId = id
    }

    public getReportEvent() {
        return _reportEventId
    }

    public setReportFocusList(id: number) {
        _reportFocusListId = id
    }

    public getReportFocusList() {
        return _reportFocusListId
    }

    public setDialogOpen(value: boolean) {
        _dialogOpen = value

        // If closing the dialog clear the events
        if (value === false) {
            _dialogEvents = []
        }
    }

    public getDialogOpen() {
        return _dialogOpen
    }

    public setDialogType(type: 'event' | 'campaign') {
        _dialogType = type
    }

    public getDialogType() {
        return _dialogType
    }

    public setDialogEvents(data: Type_ICalendarEvent[]) {
        _dialogEvents = data
    }

    public getDialogEvents() {
        return _dialogEvents
    }

    public setDialogCampaigns(data: Procedure[]) {
        _dialogCampaigns = data
    }

    public getDialogCampaigns() {
        return _dialogCampaigns
    }

    public deleteStoreData() {
        _locations = []
        _searchText = ''
        _nearbyLocations = { status: EStatus.loading }
        _searchLocations = { status: EStatus.loading, locations: [] }
        _downloadingLocations = new Set()
        _address = {
            name: '',
            street: '',
            city: '',
            postcode: '',
            state: '',
            xCoord: null,
            yCoord: null,
        }
        _timeline = { status: EStatus.loading, events: [] }
        _selectedLocation = null
        _selectedEvent = null
        _reportEventId = null
        _reportFocusListId = null
        _dialogOpen = false
        _dialogType = 'event'
        _dialogEvents = []
        _dialogCampaigns = []
    }

    public emitChange() {
        this.emit(CHANGE_EVENT)
    }

    public addChangeListener(callback: any) {
        this.on(CHANGE_EVENT, callback)
    }

    public removeChangeListener(callback: any) {
        this.removeListener(CHANGE_EVENT, callback)
    }
}

const store = new LocationStore()

// Register callback to handle all updates
Base_Dispatcher.register(function (action: any) {
    if (action.actionType === undefined) {
        throw Error('Attempting to dispatch an undefined action. Please check your constants.')
    }

    switch (action.actionType) {
        case Loca_SEARCH_LOCATION:
            store.searchLocation(action.data)
            store.emitChange()
            break

        case Loca_SET_LOCATIONS:
            store.setLocations(action.data)
            store.emitChange()
            break

        case Loca_SET_SEARCH_LOCATIONS:
            store.setSearchLocations(action.data)
            store.emitChange()
            break

        case Loca_SET_NEARBY_LOCATIONS:
            store.setNearbyLocations(action.data)
            store.emitChange()
            break

        case Loca_SET_DOWNLOADING_LOCATIONS:
            store.setDownloadingLocations(action.data)
            store.emitChange()
            break

        case Loca_SET_TIMELINE:
            store.setTimeline(action.data)
            store.emitChange()
            break

        case Loca_SELECT_LOCATION:
            store.selectLocation(action.data)
            break

        case Loca_SELECT_EVENT:
            store.selectEvent(action.data)
            store.emitChange()
            break

        case Loca_SET_ADDRESS:
            store.setAddress(action.data)
            store.emitChange()
            break

        case Loca_SET_ADDRESS_FIELD:
            store.setAddressField(action.data.field, action.data.value)
            store.emitChange()
            break

        case Loca_SET_REPORT_EVENT:
            store.setReportEvent(action.data)
            break

        case Loca_SET_REPORT_FOCUSLIST:
            store.setReportFocusList(action.data)
            break

        case Loca_SET_DIALOG_OPEN:
            store.setDialogOpen(action.data)
            store.emitChange()
            break

        case Loca_SET_DIALOG_TYPE:
            store.setDialogType(action.data)
            break

        case Loca_SET_DIALOG_EVENTS:
            store.setDialogEvents(action.data)
            break

        case Loca_SET_DIALOG_CAMPAIGNS:
            store.setDialogCampaigns(action.data)
            break

        default:
            return true
    }
})

export default store
